import { SkeletonWooDark } from '@/components/skeleton/skeleton.woo-dark';
import { useGetAccountServices, useGetPersonalInfo } from '@/hooks/api';
import { useGetMatchedRoute } from '@/hooks/routing';
import { groupServiceAccounts } from '@/routes/(services)/-model/group-service-accounts';
import { WooHome, WooTv } from '@nosinovacao/fe-icons';
import { cn, If, useI18n } from '@nosinovacao/nosid-mfe-common';
import { type NavSheetContentSections } from '../../context/navbar';
import { NavItemWooDark } from './nav-item';

export function MainNavContentWooDark({
  onClose,
  className,
  goToSection,
  currentSection,
}: Readonly<{
  onClose: () => void;
  className?: string;
  goToSection?: (section: Exclude<NavSheetContentSections, 'main'>) => void;
  currentSection?: NavSheetContentSections;
}>) {
  const { t } = useI18n();
  const match = useGetMatchedRoute();
  const { data, isLoading } = useGetPersonalInfo();
  const isTvSectionActive = currentSection === 'tv';
  return (
    <div className={cn('h-full overflow-y-auto', className)}>
      <ul className="grid pt-3">
        <NavItemWooDark
          active={match.routeId === '/(home)/home' && !isTvSectionActive}
          route={{
            path: '/home',
            params: {},
            search: {},
          }}
          icon={WooHome}
          title={t('Common.Navbar.Routes.Home')}
          onClick={onClose}
          data-tid="nav-home"
        />
        <SkeletonWooDark loading={isLoading} className="h-[72px] w-full rounded-l-lg" count={1}>
          <If condition={!!data?.HasTvService}>
            <TvNavItemWooDark
              goToSection={goToSection}
              onClose={onClose}
              isTvSectionActive={isTvSectionActive}
            />
          </If>
        </SkeletonWooDark>
      </ul>
    </div>
  );
}

function TvNavItemWooDark({
  goToSection,
  isTvSectionActive,
  onClose,
}: Readonly<{
  isTvSectionActive: boolean;
  goToSection?: (section: Exclude<NavSheetContentSections, 'main'>) => void;
  onClose: () => void;
}>) {
  const { t } = useI18n();
  const { data, isLoading } = useGetAccountServices();
  const serviceAccounts = groupServiceAccounts(data?.Holder?.ServiceAccounts ?? [])
    .filter((sa) => sa.HasTvService)
    .map((sa) => sa.Id);
  const match = useGetMatchedRoute();
  return (
    <SkeletonWooDark loading={isLoading} className="h-[72px]">
      <NavItemWooDark
        active={match.routeId.startsWith('/(tv)/tv/') || isTvSectionActive}
        data-tid="nav-tv"
        route={
          serviceAccounts.length === 1
            ? {
                path: '/tv/$id',
                params: { id: serviceAccounts[0] },
                search: {},
              }
            : undefined
        }
        icon={WooTv}
        title={t('Common.Navbar.Routes.Tv')}
        onClick={() => {
          if (serviceAccounts.length === 1) onClose();
          else goToSection?.('tv');
        }}
        canClickWhenActive
      />
    </SkeletonWooDark>
  );
}
